import Dialog from "../../components/General/ConfirmableDialog";
import { useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import PersistedDataGrid from "../../components/PersistedDataGrid/PersistedDataGrid";
import useAuth from "../../hooks/useAuth";
import { apiVariable } from "../../api/apiVariable";
import {
  createResignationLetter,
  deleteResignationLetter,
  getResignationLetters,
  updateResignationLetter,
} from "../../api/resignationLetterService";
import { resignationLettersTableColumns } from "../../data/FormColumns/HR/resignationLettersTableColumns";
import GenericFormPopup from "../../components/Fleet/GenericFormPopup/GenericFormPopup";
import { ResignationLetterDto } from "../../interfaces/HR/resignationLetter";

const fetchEmployees = async (token: string | null | undefined) => {
  const { data } = await axios.get(`${apiVariable}/getAllEmployees`, {
    headers: { Authorization: `Bearer ${token}` },
    params: { areActive: true },
  });
  return data;
};

const formFields = [
  {
    name: "employeeRefId",
    label: "Employee",
    type: "combo",
    dataKey: "employees",
    validation: { required: "Employee is required" },
  },
  {
    name: "lastDateOfWork",
    label: "Last Date Of Work",
    type: "date",
    validation: { required: "Last date of work is required" },
  },
  {
    name: "reasonOfTermination",
    label: "Reason Of Termination",
    type: "text",
    validation: { required: "Reason is required" },
  },
  { name: "comment", label: "Comment", type: "text" },
];

const initialValues = {
  employeeRefId: null,
  lastDateOfWork: "",
  reasonOfTermination: "",
  comment: "",
};

const ResignationLettersPage = () => {
  const { token } = useAuth();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [isUpdateMode, setIsUpdateMode] = useState(false);
  const [selectedLetter, setSelectedLetter] =
    useState<ResignationLetterDto | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const { data: letters = [], isLoading } = useQuery({
    queryKey: ["resignationLetters"],
    queryFn: () => getResignationLetters(token!),
    enabled: !!token,
  });

  const { data: employees = [] } = useQuery({
    queryKey: ["employeesForResignationLetters"],
    queryFn: () => fetchEmployees(token),
    enabled: !!token,
  });

  const employeeOptions = useMemo(
    () =>
      employees.map((employee: any) => ({
        id: employee.id,
        label: `${employee.firstName} ${employee.lastName}`,
        firstName: employee.firstName,
        lastName: employee.lastName,
      })),
    [employees],
  );

  const filteredLetters = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return letters;

    return letters.filter((letter: any) =>
      [letter.employeeFirstName, letter.employeeLastName].some((field) =>
        field?.toString().toLowerCase().includes(term),
      ),
    );
  }, [letters, searchTerm]);

  const saveMutation = useMutation({
    mutationFn: (data: any) =>
      isUpdateMode
        ? updateResignationLetter(token!, {
            ...data,
            id: selectedLetter?.id,
            employeeRefId: data.employeeRefId.id,
            employeeFirstName: data.employeeRefId.firstName,
            employeeLastName: data.employeeRefId.lastName,
          })
        : createResignationLetter(token!, {
            ...data,
            employeeRefId: data.employeeRefId.id,
            employeeFirstName: data.employeeRefId.firstName,
            employeeLastName: data.employeeRefId.lastName,
          }),
    onSuccess: () => {
      setOpen(false);
      setIsUpdateMode(false);
      setSelectedLetter(null);
      queryClient.invalidateQueries({ queryKey: ["resignationLetters"] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteResignationLetter(token!, id),
    onSuccess: () => {
      setDeleteConfirmOpen(false);
      setSelectedLetter(null);
      queryClient.invalidateQueries({ queryKey: ["resignationLetters"] });
    },
  });

  const rowsWithActions = useMemo(
    () =>
      filteredLetters.map((letter: ResignationLetterDto) => ({
        ...letter,
        actions: (
          <Box
            sx={{
              flexWrap: { xs: "wrap", lg: "nowrap" },
              display: "flex",
              gap: 1,
            }}
          >
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                setSelectedLetter(letter);
                setIsUpdateMode(true);
                setOpen(true);
              }}
            >
              Update
            </Button>
            <Button
              size="small"
              color="error"
              variant="outlined"
              onClick={() => {
                setSelectedLetter(letter);
                setDeleteConfirmOpen(true);
              }}
            >
              Delete
            </Button>
          </Box>
        ),
      })),
    [filteredLetters],
  );

  const tableColumns = useMemo(
    () => [
      ...resignationLettersTableColumns,
      {
        field: "actions",
        headerName: "",
        width: 220,
        sortable: false,
        filterable: false,
        renderCell: (params: any) => params.row.actions,
      },
    ],
    [],
  );

  const updateInitialValues = useMemo(() => {
    if (!selectedLetter || !isUpdateMode) return initialValues;

    return {
      employeeRefId:
        employeeOptions.find(
          (option: { id: string }) =>
            option.id === selectedLetter.employeeRefId,
        ) || null,
      lastDateOfWork: selectedLetter.lastDateOfWork?.split("T")[0] || "",
      reasonOfTermination: selectedLetter.reasonOfTermination || "",
      comment: selectedLetter.comment || "",
    };
  }, [employeeOptions, isUpdateMode, selectedLetter]);

  return (
    <Box className="incidents-page">
      <Grid container spacing={1} alignItems="center" justifyContent="center">
        <Grid
          item
          xs={12}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Box
            sx={{
              flexWrap: { xs: "wrap", lg: "nowrap" },
              flex: 1,
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Grid
              container
              spacing={1}
              justifyContent="center"
              sx={{ maxWidth: "600px" }}
            >
              <Grid item>
                <Button variant="contained" onClick={() => setOpen(true)}>
                  Create Resignation Letter
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Grid>
      </Grid>

      {isLoading ? (
        <Box className="table-loader">
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <Box className="employees-table-container" height={900}>
              <Box
                sx={{
                  flexWrap: { xs: "wrap", lg: "nowrap" },
                  display: "flex",
                  justifyContent: "flex-end",
                  mb: 2,
                }}
              >
                <TextField
                  label="Search"
                  variant="outlined"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by first name, last name"
                  sx={{ maxWidth: "100%", width: 400 }}
                />
              </Box>
              <PersistedDataGrid
                tableName="resignationLetters"
                rows={rowsWithActions}
                columns={tableColumns as any}
                sx={{ height: 700 }}
              />
            </Box>
          </Grid>
        </Grid>
      )}

      <GenericFormPopup
        formFields={formFields as any}
        initialValues={updateInitialValues}
        title={
          isUpdateMode
            ? "Update Resignation Letter"
            : "Create Resignation Letter"
        }
        onSubmit={(data) => saveMutation.mutate(data)}
        open={open}
        onClose={() => {
          setOpen(false);
          setIsUpdateMode(false);
          setSelectedLetter(null);
        }}
        autocompleteData={{ employees: employeeOptions }}
        loading={saveMutation.isPending}
      />

      <Dialog
        open={deleteConfirmOpen}
        onClose={() => {
          setDeleteConfirmOpen(false);
          setSelectedLetter(null);
        }}
      >
        <DialogTitle>Delete Resignation Letter</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete resignation letter for{" "}
            <strong>
              {selectedLetter?.employeeFirstName}{" "}
              {selectedLetter?.employeeLastName}
            </strong>
            ?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDeleteConfirmOpen(false);
              setSelectedLetter(null);
            }}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={!selectedLetter || deleteMutation.isPending}
            onClick={() => {
              if (!selectedLetter) return;
              deleteMutation.mutate(selectedLetter.id);
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ResignationLettersPage;
